<script setup lang="ts">
import { ArrowDown, ArrowUp } from "@element-plus/icons-vue";
import { FormDefinition } from "~/api";
import type {
  FormVersionInfo,
  VersionFamily as Family,
} from "~/NewFormBuilder/composables/useFormVersions";
import { compareFormVersions } from "~/NewFormBuilder/composables/useFormVersions";
import type { Slotted } from "~/types/Slotted";
import { formatDate } from "~/utils/dateUtils";

const props = defineProps<{
  families: Family[];
  versionInfoByGuid: Map<string, FormVersionInfo>;
  hasSetupBasicRole: boolean;
  /** Show the reorder arrows column (workflow steps only). */
  reorderable?: boolean;
  /** Show the "Save Order" button (order was changed and there is >1 family). */
  orderChanged?: boolean;
}>();

const emit = defineEmits<{
  (e: "open", form: FormDefinition): void;
  (e: "changeOrder", family: Family, direction: number): void;
  (e: "saveOrder"): void;
  (e: "makeDefault", form: FormDefinition): void;
}>();

/** Versions of a family, newest first, for the expanded sub-table. */
function versionsNewestFirst(family: Family) {
  return [...family.versions].sort((a, b) => compareFormVersions(b, a));
}

function info(form: FormDefinition): FormVersionInfo | undefined {
  return props.versionInfoByGuid.get(form.guid);
}

/** Total responses across a family's versions. */
function familyResponseCount(family: Family) {
  return family.versions.reduce(
    (sum, v) => sum + (v.formResponseCount ?? 0),
    0,
  );
}

function canMakeDefault(family: Family, form: FormDefinition) {
  return (
    props.hasSetupBasicRole &&
    family.versions.length > 1 &&
    !!form.formCode &&
    !form.metaInfo?.defaultForFormCode
  );
}
</script>

<template>
  <el-table
    :data="families"
    row-key="key"
    style="width: 100%; overflow-x: auto"
  >
    <!-- Expand: show all versions of a multi-version family -->
    <el-table-column type="expand">
      <template #default="{ row }: Slotted<Family>">
        <div
          v-if="row.versions.length > 1"
          class="version-subtable"
          style="padding: 0 2em 0.5em 3em"
        >
          <el-table :data="versionsNewestFirst(row)" size="small">
            <el-table-column label="Version" width="80" align="center">
              <template #default="{ row: v }: Slotted<FormDefinition>">
                v{{ info(v)?.versionNumber }}
              </template>
            </el-table-column>
            <el-table-column label="Name" min-width="220">
              <template #default="{ row: v }: Slotted<FormDefinition>">
                <el-link type="primary" @click="emit('open', v)">
                  {{ v.name }}
                </el-link>
                <el-tooltip
                  v-if="v.hasActiveDraft"
                  content="This form has a draft in progress. Opening the designer resumes it."
                >
                  <el-tag type="warning" size="small">Draft</el-tag>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="Status" min-width="200">
              <template #default="{ row: v }: Slotted<FormDefinition>">
                <div class="version-status">
                  <span class="badge-slot">
                    <el-tag
                      v-if="info(v)?.isActive"
                      type="success"
                      size="small"
                      effect="dark"
                      title="This is the version new workflows will use."
                      >In use</el-tag
                    >
                  </span>
                  <span
                    v-if="info(v)?.isDefault"
                    class="marker"
                    title="Explicitly set as the version to use (Make Default)."
                    >· default</span
                  >
                  <span
                    v-if="info(v)?.isLatest"
                    class="marker"
                    title="The most recently created version."
                    >· latest</span
                  >
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Responses" width="110" align="center">
              <template #default="{ row: v }: Slotted<FormDefinition>">
                {{ v.formResponseCount ?? 0 }}
              </template>
            </el-table-column>
            <el-table-column label="Created" width="160">
              <template #default="{ row: v }: Slotted<FormDefinition>">
                {{ v.createdAt ? formatDate(v.createdAt, 1) : "-" }}
              </template>
            </el-table-column>
            <el-table-column label="Guid" width="110" align="center">
              <template #default="{ row: v }: Slotted<FormDefinition>">
                <span :title="v.guid">{{ v.guid.substring(0, 8) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="" width="130" align="center">
              <template #default="{ row: v }: Slotted<FormDefinition>">
                <el-button
                  v-if="canMakeDefault(row, v)"
                  size="small"
                  @click="emit('makeDefault', v)"
                  >Make Default</el-button
                >
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div v-else class="version-subtable-empty" style="padding: 0.5em 3em">
          <span class="comment">Only one version.</span>
        </div>
      </template>
    </el-table-column>

    <el-table-column
      v-if="reorderable"
      width="120"
      label="Order"
      align="center"
    >
      <template #header>
        <el-button
          v-if="families.length > 1 && orderChanged"
          type="primary"
          size="small"
          @click="emit('saveOrder')"
        >
          Save Order
        </el-button>
      </template>
      <template #default="{ row }: Slotted<Family>">
        <div v-if="hasSetupBasicRole" class="order-arrows">
          <el-button
            :icon="ArrowUp"
            size="small"
            @click="emit('changeOrder', row, -1)"
          />
          <el-button
            :icon="ArrowDown"
            size="small"
            @click="emit('changeOrder', row, 1)"
          />
        </div>
      </template>
    </el-table-column>

    <el-table-column label="Name" min-width="320">
      <template #default="{ row }: Slotted<Family>">
        <el-link type="primary" @click="emit('open', row.active)">
          {{ row.active.name }}
        </el-link>
        <el-tooltip
          v-if="row.active.hasActiveDraft"
          content="This form has a draft in progress. Opening the designer resumes it."
        >
          <el-tag type="warning" size="small">Draft</el-tag>
        </el-tooltip>
        <el-tag
          v-if="row.versions.length > 1"
          size="small"
          type="info"
          style="margin-left: 8px"
        >
          {{ row.versions.length }} versions
        </el-tag>
        <div v-if="row.active.formCode" class="formCode" title="Form Code">
          <span style="margin-right: 14px">{{ row.active.formCode }}</span>
          <span
            v-if="row.active.metaInfo?.defaultForFormCode"
            class="defaultFormCode"
            >(Default)</span
          >
        </div>
      </template>
    </el-table-column>

    <el-table-column label="Active Version" width="130" align="center">
      <template #default="{ row }: Slotted<Family>">
        v{{ info(row.active)?.versionNumber ?? 1 }}
      </template>
    </el-table-column>

    <el-table-column label="Responses" width="110" align="center">
      <template #default="{ row }: Slotted<Family>">
        {{ familyResponseCount(row) }}
      </template>
    </el-table-column>

    <!-- Caller-injected columns (workflow-step specifics, etc.) -->
    <slot name="extra-columns" />

    <el-table-column label="Updated" width="160">
      <template #default="{ row }: Slotted<Family>">
        {{ row.active.updatedAt ? formatDate(row.active.updatedAt, 1) : "-" }}
      </template>
    </el-table-column>
  </el-table>
</template>

<style scoped lang="less">
.formCode {
  font-size: 0.85em;
  color: var(--el-text-color-secondary);
  margin-top: 2px;
}
.defaultFormCode {
  color: var(--el-color-primary);
  font-weight: 600;
}
.order-arrows {
  display: flex;
  gap: 4px;
  justify-content: center;
}
.comment {
  color: var(--el-text-color-secondary);
  font-style: italic;
}
.version-status {
  display: flex;
  align-items: center;
  gap: 8px;
  // Fixed slot so the markers line up whether or not the "In use" badge is present.
  .badge-slot {
    display: inline-flex;
    min-width: 62px;
  }
  .marker {
    color: var(--el-text-color-secondary);
    font-size: 0.8em;
  }
}
</style>
